/**
 * Synergistic Health Effects Module for Care360 Biological Age Calculation
 * 
 * This module identifies and models parameter combinations that have amplified
 * protective or destructive impacts beyond their individual contributions.
 * 
 * Evidence-based synergistic relationships:
 * - Metabolic Syndrome Components (glucose, BP, lipids, BMI, inflammation)
 * - Cardiovascular Risk Clusters (lipids, BP, exercise, BMI)
 * - Inflammation-Lifestyle Interactions (CRP, stress, sleep, exercise)
 * - Immune-Lifestyle Synergies (WBC, lymphocytes, stress, nutrition)
 */



// Synergistic effect constants
const SYNERGY_CONSTANTS = {
  // Maximum synergistic multipliers (bounded to prevent unrealistic adjustments)
  MAX_PROTECTIVE_MULTIPLIER: 1.5,    // 50% amplification of protective effects
  MAX_DESTRUCTIVE_MULTIPLIER: 2.0,   // 100% amplification of destructive effects
  
  // Threshold requirements for synergistic activation
  MIN_PARAMETERS_FOR_SYNERGY: 3,     // Minimum parameters needed in cluster
  EXCELLENT_THRESHOLD: 90,            // Health score threshold for protective synergy
  POOR_THRESHOLD: 60,                 // Health score threshold for destructive synergy
  
  // Synergistic contribution limits (years)
  MAX_SYNERGISTIC_BENEFIT: -2.0,     // Maximum anti-aging benefit from synergies
  MAX_SYNERGISTIC_PENALTY: 3.0       // Maximum aging penalty from synergies
};

/**
 * Evidence-based parameter clusters for synergistic effects
 */
const SYNERGISTIC_CLUSTERS = {
  // Metabolic Syndrome Cluster (Strong clinical evidence)
  metabolic_syndrome: {
    name: "Metabolic Syndrome",
    description: "Glucose metabolism, blood pressure, lipids, and inflammation",
    evidence_level: "high",
    parameters: {
      core: [
        'labs_glucose_glucose',
        'vitals_sbp',
        'vitals_dbp', 
        'labs_lipids_hdl',
        'labs_lipids_ldl',
        'labs_lipids_triglycerides',
        'biometrics_bmi'
      ],
      supporting: [
        'labs_inflammation_hscrp',
        'labs_glucose_hba1c',
        'biometrics_waist_circumference'
      ]
    },
    thresholds: {
      protective: { min_excellent: 4, min_good: 6 },
      destructive: { min_poor: 3, min_concerning: 5 }
    },
    multipliers: {
      protective: 1.3,
      destructive: 1.8
    }
  },

  // Cardiovascular Risk Cluster
  cardiovascular_risk: {
    name: "Cardiovascular Risk",
    description: "Lipid profile, blood pressure, exercise, and body composition",
    evidence_level: "high", 
    parameters: {
      core: [
        'labs_lipids_hdl',
        'labs_lipids_ldl',
        'vitals_sbp',
        'vitals_dbp',
        'lifestyle_movement_cardio_weekly_minutes'
      ],
      supporting: [
        'biometrics_bmi',
        'labs_inflammation_hscrp',
        'lifestyle_movement_strength_weekly_sessions'
      ]
    },
    thresholds: {
      protective: { min_excellent: 3, min_good: 5 },
      destructive: { min_poor: 3, min_concerning: 4 }
    },
    multipliers: {
      protective: 1.4,
      destructive: 1.6
    }
  },

  // Inflammation-Lifestyle Cluster
  inflammation_lifestyle: {
    name: "Inflammation-Lifestyle",
    description: "Inflammatory markers with stress, sleep, and exercise interactions",
    evidence_level: "moderate",
    parameters: {
      core: [
        'labs_inflammation_hscrp',
        'lifestyle_recovery_sleep_quality',
        'lifestyle_emotion_stress_level',
        'lifestyle_movement_cardio_weekly_minutes'
      ],
      supporting: [
        'labs_cbc_wbc',
        'lifestyle_nutrition_diet_quality',
        'lifestyle_recovery_sleep_hours'
      ]
    },
    thresholds: {
      protective: { min_excellent: 3, min_good: 4 },
      destructive: { min_poor: 2, min_concerning: 3 }
    },
    multipliers: {
      protective: 1.2,
      destructive: 1.4
    }
  },

  // Immune-Lifestyle Cluster
  immune_lifestyle: {
    name: "Immune-Lifestyle",
    description: "Immune markers with nutrition, stress, and recovery interactions",
    evidence_level: "moderate",
    parameters: {
      core: [
        'labs_cbc_wbc',
        'labs_cbc_lymph_pct',
        'lifestyle_nutrition_diet_quality',
        'lifestyle_emotion_stress_level'
      ],
      supporting: [
        'labs_protein_albumin',
        'lifestyle_recovery_sleep_quality',
        'lifestyle_movement_cardio_weekly_minutes'
      ]
    },
    thresholds: {
      protective: { min_excellent: 3, min_good: 4 },
      destructive: { min_poor: 2, min_concerning: 3 }
    },
    multipliers: {
      protective: 1.2,
      destructive: 1.3
    }
  }
};

/**
 * Get parameter health score from system deviations
 */
function getParameterHealthScore(paramKey, systemDeviations, lifestyleDomainDeviations) {
  // Search through all systems and dimensions for the parameter
  for (const systemKey in systemDeviations) {
    for (const dimKey in systemDeviations[systemKey]) {
      const contributingParams = systemDeviations[systemKey][dimKey].contributingParams || [];
      const param = contributingParams.find(p => p.key === paramKey);
      if (param) {
        // Calculate individual parameter health score from Z-score
        const absZScore = Math.abs(param.zScore || 0);
        return Math.max(0, 100 - (absZScore / 6.0) * 100);
      }
    }
  }

  // Search through lifestyle domains
  for (const domainKey in lifestyleDomainDeviations) {
    const contributingParams = lifestyleDomainDeviations[domainKey].contributingParams || [];
    const param = contributingParams.find(p => p.key === paramKey);
    if (param) {
      const absZScore = Math.abs(param.zScore || 0);
      return Math.max(0, 100 - (absZScore / 6.0) * 100);
    }
  }

  return null; // Parameter not found
}

/**
 * Evaluate synergistic cluster for protective or destructive effects
 */
function evaluateClusterSynergy(cluster, systemDeviations, lifestyleDomainDeviations) {
  const parameterScores = {};
  let availableParameters = 0;

  // Collect health scores for all parameters in cluster
  [...cluster.parameters.core, ...cluster.parameters.supporting].forEach(paramKey => {
    const score = getParameterHealthScore(paramKey, systemDeviations, lifestyleDomainDeviations);
    if (score !== null) {
      parameterScores[paramKey] = score;
      availableParameters++;
    }
  });

  // Need minimum parameters for synergy
  if (availableParameters < SYNERGY_CONSTANTS.MIN_PARAMETERS_FOR_SYNERGY) {
    return { type: 'none', multiplier: 1.0, details: 'Insufficient parameters' };
  }

  // Count parameters in different health categories
  const excellentParams = Object.values(parameterScores).filter(score => score >= SYNERGY_CONSTANTS.EXCELLENT_THRESHOLD).length;
  const goodParams = Object.values(parameterScores).filter(score => score >= 80 && score < SYNERGY_CONSTANTS.EXCELLENT_THRESHOLD).length;
  const poorParams = Object.values(parameterScores).filter(score => score <= SYNERGY_CONSTANTS.POOR_THRESHOLD).length;
  const concerningParams = Object.values(parameterScores).filter(score => score > SYNERGY_CONSTANTS.POOR_THRESHOLD && score < 80).length;

  // Evaluate protective synergy
  if (excellentParams >= cluster.thresholds.protective.min_excellent || 
      (excellentParams + goodParams) >= cluster.thresholds.protective.min_good) {
    return {
      type: 'protective',
      multiplier: cluster.multipliers.protective,
      details: `${excellentParams} excellent, ${goodParams} good parameters`,
      parameterScores,
      excellentParams,
      goodParams
    };
  }

  // Evaluate destructive synergy  
  if (poorParams >= cluster.thresholds.destructive.min_poor ||
      (poorParams + concerningParams) >= cluster.thresholds.destructive.min_concerning) {
    return {
      type: 'destructive', 
      multiplier: cluster.multipliers.destructive,
      details: `${poorParams} poor, ${concerningParams} concerning parameters`,
      parameterScores,
      poorParams,
      concerningParams
    };
  }

  return { type: 'none', multiplier: 1.0, details: 'No synergistic threshold met' };
}

/**
 * Calculate synergistic health effects for biological age adjustment
 */
export function calculateSynergisticEffects(systemDeviations, lifestyleDomainDeviations) {
  const synergisticResults = {
    totalSynergisticAdjustment: 0,
    clusterResults: {},
    appliedEffects: [],
    summary: {
      protectiveClusters: 0,
      destructiveClusters: 0,
      totalProtectiveBenefit: 0,
      totalDestructivePenalty: 0
    }
  };

  // Evaluate each synergistic cluster
  for (const [clusterKey, cluster] of Object.entries(SYNERGISTIC_CLUSTERS)) {
    const evaluation = evaluateClusterSynergy(cluster, systemDeviations, lifestyleDomainDeviations);
    synergisticResults.clusterResults[clusterKey] = evaluation;

    if (evaluation.type !== 'none') {
      // Calculate base effect from individual system contributions
      let baseEffect = 0;
      
      // Estimate base effect from cluster parameters (simplified approach)
      if (evaluation.parameterScores) {
        const avgScore = Object.values(evaluation.parameterScores).reduce((sum, score) => sum + score, 0) / Object.values(evaluation.parameterScores).length;
        
        if (evaluation.type === 'protective') {
          // Base protective effect (negative = anti-aging)
          baseEffect = -((avgScore - 85) / 15) * 0.5; // Up to -0.5 years base
        } else {
          // Base destructive effect (positive = aging)
          baseEffect = ((75 - avgScore) / 75) * 1.0; // Up to +1.0 years base
        }
      }

      // Apply synergistic multiplier
      const synergisticEffect = baseEffect * (evaluation.multiplier - 1.0); // Only the amplification portion
      
      // Apply bounds
      const boundedEffect = Math.max(
        SYNERGY_CONSTANTS.MAX_SYNERGISTIC_BENEFIT,
        Math.min(SYNERGY_CONSTANTS.MAX_SYNERGISTIC_PENALTY, synergisticEffect)
      );

      synergisticResults.totalSynergisticAdjustment += boundedEffect;
      synergisticResults.appliedEffects.push({
        cluster: cluster.name,
        type: evaluation.type,
        baseEffect,
        multiplier: evaluation.multiplier,
        synergisticEffect: boundedEffect,
        details: evaluation.details
      });

      // Update summary
      if (evaluation.type === 'protective') {
        synergisticResults.summary.protectiveClusters++;
        synergisticResults.summary.totalProtectiveBenefit += boundedEffect;
      } else {
        synergisticResults.summary.destructiveClusters++;
        synergisticResults.summary.totalDestructivePenalty += boundedEffect;
      }
    }
  }

  return synergisticResults;
}

/**
 * Get detailed explanation of synergistic effects for documentation
 */
export function getSynergisticEffectsExplanation(synergisticResults) {
  const explanations = [];
  
  synergisticResults.appliedEffects.forEach(effect => {
    const direction = effect.type === 'protective' ? 'reduces' : 'increases';
    const years = Math.abs(effect.synergisticEffect).toFixed(2);
    
    explanations.push(
      `${effect.cluster}: ${effect.details} - ${direction} biological age by ${years} years (${effect.multiplier}x amplification)`
    );
  });

  return explanations;
}
